<template>
  <router-view />
</template>

<script setup lang="ts">
import { onMounted } from 'vue';
import { userSettings } from '@/stores/userSettings';
import { preloadCriticalAssets, preloadProjectImagesInBackground } from '@/utils/assetPreloader';
import '@/styles/theme.css';

// Initialize theme on app load and preload assets
// Build trigger: Cloudflare Pages deployment
onMounted(async () => {
  userSettings.initializeTheme();

  // Preload critical assets (rabbit, pixel) immediately
  await preloadCriticalAssets();

  // Start preloading project images in background
  preloadProjectImagesInBackground();
});
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  overflow: hidden;
  background: var(--bg-primary);
  font-family: Inter, Arial, sans-serif;
}

#app {
  width: 100vw;
  height: 100vh;
}

/* ============================================
   TUTORIAL HIGHLIGHTING SYSTEM
   ============================================
   When tutorial is active, everything dims except highlighted elements.
   Classes are applied to <body> by TutorialOverlay.vue
   Canvas elements are controlled via Pixi.js container alpha.
*/

/* Hide only the about modal and minimized bar during tutorial - allow other modals to open */
body.tutorial-active .about-modal,
body.tutorial-active .minimized-tabs-bar {
  display: none !important;
}

/* Base: dim header when tutorial is active */
body.tutorial-active .header-bar {
  opacity: 0.15 !important;
  transition: opacity 0.3s ease;
}

/* Canvas opacity is controlled by Pixi.js, not CSS */

/* Step 1: Search bar - brighten and pulse glow */
body.tutorial-active.tutorial-highlight-search .header-bar {
  opacity: 1 !important;
}

body.tutorial-active.tutorial-highlight-search .search-container {
  position: relative;
}

body.tutorial-active.tutorial-highlight-search .search-container::after {
  content: '';
  position: absolute;
  inset: -3px;
  border: 3px solid rgba(16, 185, 129, 1);
  border-radius: 11px;
  pointer-events: none;
  animation: tutorialBorderPulse 2s ease-in-out infinite;
}

/* Dim controls when only search is highlighted (not stats) */
body.tutorial-active.tutorial-highlight-search:not(.tutorial-highlight-stats) .controls {
  opacity: 0.15 !important;
}

/* Step 3: Stats are Pixi elements - glow handled in useVisualization.ts */

/* Step 4: Enlist - brighten just the header for the button */
body.tutorial-active.tutorial-highlight-enlist .header-bar {
  opacity: 1 !important;
}

/* Dim everything in header except the enlist button */
body.tutorial-active.tutorial-highlight-enlist .search-container,
body.tutorial-active.tutorial-highlight-enlist .controls {
  opacity: 0.15 !important;
}

/* Step 4: Enlist button - just visible, no special effect needed */

/* Green border pulse: opacity 0.3 to 1.0 matching Pixi sine wave */
@keyframes tutorialBorderPulse {
  0%, 100% {
    opacity: 0.3;
  }
  50% {
    opacity: 1;
  }
}
</style>
